import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../../lib/api';
import { DollarSign, ClipboardList, CheckCircle2, AlertTriangle, FileText, CalendarDays, User, MapPin, Phone, Mail, AlertCircle, Calendar, FileWarning, Wallet, Zap, TrendingUp, FilePlus2, Receipt, CreditCard, ArrowDownRight } from 'lucide-react';
import { StageBadge, formatCurrency } from '../../pages/ProjectsList';

export function ProjectOverview({ project, onRefresh, teamUsers }) {
  if (!project) return null;
  const s = project.summary || {};

  const contractValue = project.contract_value || 0;
  const approvedCOs = s.co_approved_total || 0;
  const adjustedContract = contractValue + approvedCOs;
  const budgetCost = s.budget_total_cost || 0;
  const margin = adjustedContract > 0
    ? ((adjustedContract - budgetCost) / adjustedContract * 100).toFixed(1)
    : null;
  const invoiced = s.invoiced_total || 0;
  const collected = s.collected_total || 0;
  const receivable = invoiced - collected;
  const hasFinancialActivity = invoiced > 0 || collected > 0;

  // Build "Needs Attention" alerts
  const alerts = [];
  if (!project.customer) alerts.push({ icon: User, text: 'No customer assigned', type: 'warning' });
  if (!project.customer?.address) alerts.push({ icon: MapPin, text: 'No project location set', type: 'info' });
  if (s.budget_total_cost === 0) alerts.push({ icon: Wallet, text: 'Budget is empty', type: 'info' });
  if (s.task_overdue > 0) alerts.push({ icon: AlertTriangle, text: `${s.task_overdue} overdue task${s.task_overdue > 1 ? 's' : ''}`, type: 'error' });
  if (s.task_total === 0) alerts.push({ icon: ClipboardList, text: 'No tasks created yet', type: 'info' });
  if (s.log_count === 0) alerts.push({ icon: CalendarDays, text: 'No daily logs yet', type: 'info' });
  if (!project.start_date) alerts.push({ icon: Calendar, text: 'No start date set', type: 'info' });
  if (!project.contract_value) alerts.push({ icon: DollarSign, text: 'No contract value set', type: 'info' });

  return (
    <div className="space-y-6" data-testid="project-overview">
      {/* Needs Attention Banner */}
      {alerts.length > 0 && (
        <div className="border rounded-lg overflow-hidden" data-testid="needs-attention">
          <div className="bg-amber-50 dark:bg-amber-900/10 border-b border-amber-200 dark:border-amber-800/40 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Needs Attention
            </h3>
          </div>
          <div className="p-3 space-y-1">
            {alerts.map((a, i) => {
              const Icon = a.icon;
              const color = a.type === 'error' ? 'text-red-600 dark:text-red-400' : a.type === 'warning' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground';
              return (
                <div key={i} className={`flex items-center gap-2 text-sm ${color}`} data-testid={`alert-${i}`}>
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{a.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Applied Workflow Indicator */}
      {project.applied_workflow_name && (
        <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-primary/5 text-sm" data-testid="applied-workflow-indicator">
          <Zap className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-muted-foreground">Workflow:</span>
          <span className="font-medium">{project.applied_workflow_name}</span>
        </div>
      )}

      {/* Customer & Location — Prominent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border rounded-lg p-5" data-testid="project-customer-card">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">Customer & Location</h3>
          {project.customer ? (
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-primary/70" />
                  <span className="text-lg font-semibold">{project.customer.name}</span>
                </div>
                {project.customer.phone && (
                  <a href={`tel:${project.customer.phone}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                    <Phone className="w-4 h-4" />{project.customer.phone}
                  </a>
                )}
                {project.customer.email && (
                  <a href={`mailto:${project.customer.email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                    <Mail className="w-4 h-4" />{project.customer.email}
                  </a>
                )}
              </div>
              {project.customer.address && (
                <div className="flex-1">
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                    <span>{project.customer.address}</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">No customer assigned — add one to track contact and location info.</p>
          )}
        </div>

        {/* Key Dates */}
        <div className="border rounded-lg p-5" data-testid="project-dates-card">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-3">Project Timeline</h3>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Stage</p>
              <StageBadge stage={project.project_stage} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Start</p>
                <p className="font-medium">{project.start_date || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Target End</p>
                <p className="font-medium">{project.target_end_date || '—'}</p>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Priority</p>
              <p className="font-medium capitalize">{project.priority}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Summary Cards — Row 1: Contract & Budget */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard icon={DollarSign} label="Original Contract" value={formatCurrency(contractValue)} />
        <SummaryCard
          icon={FilePlus2}
          label="Change Orders"
          value={approvedCOs !== 0 ? formatCurrency(approvedCOs) : '—'}
          subtext={s.co_count > 0 ? `${s.co_count} total` : null}
        />
        <SummaryCard
          icon={TrendingUp}
          label="Adjusted Contract"
          value={adjustedContract > 0 ? formatCurrency(adjustedContract) : '—'}
          subtext={approvedCOs !== 0 ? (approvedCOs > 0 ? `+${formatCurrency(approvedCOs)}` : formatCurrency(approvedCOs)) : null}
        />
        <SummaryCard icon={Wallet} label="Budget Cost" value={formatCurrency(budgetCost)} subtext={margin ? `${margin}% margin` : null} />
        <SummaryCard
          icon={ClipboardList}
          label="Tasks"
          value={s.task_total > 0 ? `${s.task_done}/${s.task_total}` : '0'}
          subtext={s.task_overdue > 0 ? `${s.task_overdue} overdue` : s.task_total > 0 ? 'on track' : null}
          alert={s.task_overdue > 0}
        />
      </div>

      {/* Financial Summary Cards — Row 2: Billing (only if financial activity exists) */}
      {hasFinancialActivity && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="billing-cards">
          <SummaryCard
            icon={Receipt}
            label="Invoiced"
            value={formatCurrency(invoiced)}
            subtext={adjustedContract > 0 ? `${((invoiced / adjustedContract) * 100).toFixed(0)}% of contract` : null}
            accent="blue"
          />
          <SummaryCard
            icon={CreditCard}
            label="Collected"
            value={formatCurrency(collected)}
            subtext={invoiced > 0 ? `${((collected / invoiced) * 100).toFixed(0)}% of invoiced` : null}
            accent="green"
          />
          <SummaryCard
            icon={ArrowDownRight}
            label="Receivable"
            value={receivable > 0 ? formatCurrency(receivable) : '—'}
            alert={receivable > 0}
            subtext={receivable > 0 ? 'outstanding' : 'all collected'}
            accent="amber"
          />
        </div>
      )}

      {/* Description & Notes */}
      {(project.description || project.notes) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {project.description && (
            <div className="border rounded-lg p-5">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-2">Scope / Description</h3>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{project.description}</p>
            </div>
          )}
          {project.notes && (
            <div className="border rounded-lg p-5">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-2">Notes</h3>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{project.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Quick Info Footer */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span>Job #{project.job_number}</span>
        <span>Status: <span className="capitalize">{project.status}</span></span>
        {project.created_at && <span>Created: {new Date(project.created_at).toLocaleDateString()}</span>}
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, subtext, alert, accent }) {
  const accentStyles = {
    blue: 'border-blue-200 dark:border-blue-800/50 bg-blue-50/40 dark:bg-blue-950/20',
    green: 'border-green-200 dark:border-green-800/50 bg-green-50/40 dark:bg-green-950/20',
    amber: 'border-amber-200 dark:border-amber-800/50 bg-amber-50/40 dark:bg-amber-950/20',
  };
  const iconAccent = {
    blue: 'text-blue-500 dark:text-blue-400',
    green: 'text-green-500 dark:text-green-400',
    amber: 'text-amber-500 dark:text-amber-400',
  };
  const base = accent ? accentStyles[accent] || '' : '';
  const iconColor = accent ? iconAccent[accent] || 'text-muted-foreground' : 'text-muted-foreground';
  return (
    <div className={`border rounded-lg p-4 ${accent ? `shadow-sm ${base}` : ''}`} data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold tabular-nums">{value ?? '—'}</p>
      {subtext && (
        <p className={`text-xs mt-0.5 ${alert ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
          {alert && <AlertTriangle className="w-3 h-3 inline mr-1" />}{subtext}
        </p>
      )}
    </div>
  );
}
